/*
 * Created on Wed Mar 10 2021
 *
 * Copyright (c) 2021 Benefits Science Technologies
 */
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { AppConfig } from 'AppConfig';
import tableauImg4 from 'assets/svg/chart-bar-claims-loss-ratio.svg';
import tableauImg1 from 'assets/svg/chart-bar-claims-year-over-year.svg';
import tableauImg2 from 'assets/svg/chart-line-premiums-v-claims.svg';
import tableauImg6 from 'assets/svg/map-usa.svg';
import tableauImg11 from 'assets/svg/symbol-hashmark.svg';
import tableauImg10 from 'assets/svg/symbol-percentage.svg';
import axios from 'axios';
import Carousel from 'common/bstCarousel';
import { FoMaterialTable } from 'common/FoMaterialTable';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import * as AppActionTypes from 'store/actions/appstate';
import { store } from 'store/store';
import { hasPermission } from 'utils/common';
import { sortListInPlace, twoLevelsortListInPlace } from '../../utils/genericObjectSorter';
import * as Actions from './actions/actions';
import MetricPanelCard from './component/metricPanelCard';
import { fetchFeatureReports } from './defaultSetting/actions/action';

const SECTION = AppConfig.REPORT_CATEGORY;

const METRICS_IMAGES = {
  tableauImg4,
  tableauImg1,
  tableauImg2,
  tableauImg6,
  tableauImg11,
  tableauImg10
};

// Hide 'A-Z' from sort options until SC have been updated with Display Text
const SORT_OPTIONS = ['Trend', 'Latest Release', 'Most Viewed', 'BenInsights Pick'];
const SORT_FIELDS = ['rankTrend', 'scId', 'userClickRate', 'globalOrder'];
const FIELD_SORT_ORDER_DEFAULTS = ['asc', 'desc', 'desc', 'asc'];

const isAvailable = (arr) => {
  return arr && Array.isArray(arr) && arr.length > 0;
};

const Reports = () => {
  const currentApp = useSelector((state) => state.user.currentApp);
  const allSmarts = useSelector((state) => state.fo.smarts);
  const smarts = allSmarts?.filter((s) => s.category === AppConfig.REPORT_CATEGORY.SMART.code) || [];
  const metrics = useSelector((state) => state.fo.metrics);
  const defaultMetrics = useSelector((state) => state.fo.defaultMetrics);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);

  const [selectedMetrics, setSelectedMetrics] = useState(new Set());
  const [clonedSelected, setClonedSelected] = useState(new Set());
  const [openMetric, setOpenMetric] = useState(false);
  const [metrixPanelClosed, setMetrixPanelClosed] = useState(true);
  const [chartRecoveryModal, setChartRecoveryModal] = useState(false);

  /* Smart Cards feature bar properties*/
  const [scSortProperty, setScSortProperty] = useState(SORT_OPTIONS[0]);
  // const isLightTouch = useFeatureFlag('light-touch');

  /**
   * Generic sorter for smart card objects 
   * @param {*} propertyName
   */
  const sortSmartCardsByProp = (propertyName) => {
    switch (propertyName) {
      case SORT_OPTIONS[0]:
        sortListInPlace(smarts, SORT_FIELDS[0], FIELD_SORT_ORDER_DEFAULTS[0])
        break;
      case SORT_OPTIONS[1]:
        sortSmartCardByScId(SORT_FIELDS[1])
        break;
      case SORT_OPTIONS[2]:
        twoLevelsortListInPlace(smarts, SORT_FIELDS[2], FIELD_SORT_ORDER_DEFAULTS[2], SORT_FIELDS[3], FIELD_SORT_ORDER_DEFAULTS[3])
        break;
      case SORT_OPTIONS[3]:
        sortListInPlace(smarts, SORT_FIELDS[3], FIELD_SORT_ORDER_DEFAULTS[3])
        break;
      default:
        break;
    }
    setScSortProperty(propertyName);
  };

  /**
   * Sorts the smart card list by SCID field.
   * 
   * @param {*} smartCardField 
   */
  function sortSmartCardByScId(smartCardField) {
    return smarts.sort((s_card1, s_card2) => {
      return parseInt(s_card2[smartCardField].replace("sc", ""), 10) - parseInt(s_card1[smartCardField].replace("sc", ""), 10);
    });
  };

  // Effect to sort smarts whenever smarts changes for initial load
  useEffect(() => {
    if (smarts && smarts.length) {
      sortSmartCardsByProp(scSortProperty);
    }
  }, [smarts]);

  const [state, setState] = useState({
    recoverableCharts: [],
    selectedRecoverableCharts: [],
    screenWidth:
      AppConfig.SCREEN_RIGHT_VIEW_WIDE *
      (window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth)
  });

  const sliderSettings = {
    speed: 500,
    vertical: true,
    slidesToShow: 3,
    slidesToScroll: metrics?.length > 11 ? 2 : 1,
    dots: true,
    infinite: false,
    verticalSwiping: true,
    arrows: false
  };

  const handleToggle = () => {
    let portion = AppConfig.SCREEN_RIGHT_VIEW_WIDE;
    if (metrixPanelClosed === false) {
      portion = AppConfig.SCREEN_RIGHT_VIEW_NARROW;
    }
    setMetrixPanelClosed(!metrixPanelClosed);
    setState({
      ...state,
      screenWidth:
        portion *
        (window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth)
    });
  };

  const showSmartRecoverableCharts = () => {
    axios
      .post(AppConfig.FO_INITIAL_URL + '/init/getmuted', {
        category: SECTION.SMART
      })
      .then((res) => {
        setChartRecoveryModal(true);
        setState({
          ...state,
          recoverableCharts: res.data
        });
      })
      .catch((err) => {
        store.dispatch({
          type: AppActionTypes.APPSTATE_SET_APP_ERROR,
          payload: err
        });
      });
  };

  const recoverCharts = () => {
    axios
      .post(
        AppConfig.FO_INITIAL_URL + `/init/recovercharts`,
        state.selectedRecoverableCharts
      )
      .then((res) => {
        refresh();
      })
      .catch((err) => {
        console.log(err);
      });

    setChartRecoveryModal(false);
    setState({
      ...state,
      recoverableCharts: [],
      selectedRecoverableCharts: []
    });
  };

  const onRecoveryCancel = () => {
    setState({
      ...state,
      selectedRecoverableCharts: []
    });
    setChartRecoveryModal(false);
  };

  const handleSaveClick = () => {
    let temp = _.filter(defaultMetrics, (m) =>
      [...clonedSelected].includes(m.displayText)
    );
    store.dispatch(Actions.saveMyMetric(temp));
    setOpenMetric(false);
  };

  const refresh = () => {
    store.dispatch(Actions.getFoDetail(currentTenantCd));
    store.dispatch(Actions.fetchMetricsByTenant());
    store.dispatch(fetchFeatureReports());
  };

  useEffect(() => {
    if (isAvailable(metrics)) {
      const newSelected = new Set();
      metrics.forEach((m) => {
        newSelected.add(m.displayText);
      });
      setMetrixPanelClosed(true);// Work around for bug BSTP-1267
      setSelectedMetrics(newSelected);
    } else {
      setSelectedMetrics(new Set());
      setMetrixPanelClosed(true);
    }
  }, [metrics]);

  useEffect(() => {
    if (currentApp === AppConfig.APP_CODE_FO) {
      refresh();
    }
  }, [currentTenantCd, currentApp]);

  const getMetricsListHeight = useCallback(() => {
    const marginTop = 10;
    const dotPanelHeight = 46;
    const maxListItems = 3;
    return metrics?.length * (AppConfig.HOME_METRICS_CARD_HEIGHT + marginTop) >=
      maxListItems * (AppConfig.HOME_METRICS_CARD_HEIGHT + marginTop)
      ? `${maxListItems * (AppConfig.HOME_METRICS_CARD_HEIGHT + marginTop) +
      dotPanelHeight
      }px`
      : `${metrics?.length * (AppConfig.HOME_METRICS_CARD_HEIGHT + marginTop) +
      dotPanelHeight
      }px`;
  }, [metrics]);

  const handleMetricSelect = (displayText, isChecked) => {
    setClonedSelected(prev => {
      const updated = new Set(prev);
      if (isChecked) updated.add(displayText);
      else updated.delete(displayText);
      return updated;
    });
  };

  return (
    <Box
      pt="25px"
      width="100%"
      bgcolor="#FAFAFA"
      pb={'100px'}
      sx={{
        display: 'flex',
        minHeight: window.innerHeight * 0.78
      }}>
      {/* {!isLightTouch && metrixPanelClosed === true && (
        <Box
          sx={{
            minWidth: '70px',
            flexDirection: 'column',
            marginLeft: '20px'
          }}>
          <Typography
            style={{
              color: '#387EC7',
              fontSize: '9px',
              fontWeight: 900,
              marginBottom: '5px',
              fontFamily: 'Open Sans'
            }}>
            METRICS
          </Typography>
          <IconButton
            size="small"
            style={{
              color: '#838383',
              background: '#E6EAEF',
              marginLeft: '8px'
            }}
            onClick={() => handleToggle()}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )} 
      {!isLightTouch && !metrixPanelClosed && (
        <Box
          sx={{
            minWidth: '340px',
            maxWidth: '340px'
          }}>
          <Box
            sx={{
              bgcolor: 'white',
              marginRight: '20px',
              marginLeft: '20px',
              border: '1px solid #d3d3d3'
            }}>
            <Box
              sx={{
                borderBottom: '1px solid #d3d3d3',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingBottom: '9px',
                paddingTop: '9px'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: '7px',
                  marginLeft: '10px'
                }}>
                <IconButton
                  style={{
                    color: '#a3a3a3',
                    background: '#E6EAEF',

                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    marginRight: '10px'
                  }}
                  onClick={() => handleToggle()}
                  size="large">
                  <ChevronLeftIcon style={{ fontSize: 26 }} />
                </IconButton>

                <Typography
                  style={{
                    color: '#387EC7',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    fontFamily: 'Open Sans',
                    marginTop: '5px'
                  }}>
                  MY METRICS
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setClonedSelected(new Set(selectedMetrics));
                  setOpenMetric(true);
                }}
                size="large">
                <SettingsIcon style={{ color: '#222222' }} />
              </IconButton>
            </Box>
            <Box
              style={{
                minHeight: getMetricsListHeight(),
                maxHeight: getMetricsListHeight(),
                overflow: 'hidden'
              }}>
              <Slider {...sliderSettings}>
                {metrics &&
                  metrics.map((im, k) => (
                    <BstMetricsCard
                      key={'metric-' + k}
                      item={im}
                      height={AppConfig.HOME_METRICS_CARD_HEIGHT}
                    />
                  ))}
              </Slider>
            </Box>
          </Box>
        </Box>
      )} */}
      {hasPermission(AppConfig.PERMISSION.FO_SMART_CARD_VIEW, permissions) && (
      <Box
        bgcolor="#FAFAFA"
        sx={{
          flexDirection: 'row',
          minWidth: metrixPanelClosed
            ? window.innerWidth * 0.9
            : window.innerWidth * 0.71
        }}>
        {/* {!isLightTouch && <FeatureBar />} */}
        <Box
          sx={{
            minWidth: metrixPanelClosed
              ? window.innerWidth * 0.9
              : window.innerWidth * 0.71
          }}>
          <Grid container direction="column" alignItems="baseline" style={{ marginTop: 5 }}>
            <Grid container>
              <Grid item xs>
                <Tooltip title="Bring attention to areas in which there is potential for savings." placement="top-start">
                 
                    <Typography variant='h1' sx={{ fontFamily: 'Rubik', fontSize: '1.75rem', fontWeight: '500', ml: { lg: '3rem', xl: '10rem '}}}>
                      Smart Cards
                    </Typography>
                 
                    
                </Tooltip>
              </Grid>
              {/* {!isLightTouch && <Grid item xs={6} style={{ alignSelf: "right" }}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  width="80%"
                >
                  <InputLabel id="label_smart-card-sort" style={{ marginRight: 5, marginTop: 7}}>Sort By:</InputLabel>
                  <Box sx={{ width: 165 }}>
                    <Autocomplete
                      key="smart-card-sort"
                      value={scSortProperty}
                      options={SORT_OPTIONS}
                      getOptionLabel={(option) => option}
                      disableClearable
                      onChange={(_, newValue) => {
                        sortSmartCardsByProp(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Sort By"
                          InputProps={{
                          ...params.InputProps,
                          startAdornment: <Sort color="primary" />,
                          endAdornment: <>{params.InputProps.endAdornment}</>
                        }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Grid>} */}
            </Grid>
            <Grid sx={ { marginTop: 4 }}>
              <div className="has-carousel smart-carousel">
                <Carousel
                  items={smarts}
                  type={AppConfig.SMART_CAROUSEL}
                />
              </div>
            </Grid>
          </Grid>
        </Box>
      </Box>
      )}
      {hasPermission(
        AppConfig.PERMISSION.FO_SMARTCARD_RECOVER,
        permissions
      ) && (
          <>
            <Dialog
              fullWidth
              maxWidth="sm"
              open={chartRecoveryModal}
              onClose={onRecoveryCancel}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description">
              <DialogTitle id="alert-dialog-title">Recover Charts</DialogTitle>
              <DialogContent>
                <FoMaterialTable
                  columns={[
                    { title: 'Chart Name', field: 'name' },
                    {
                      title: 'Position',
                      field: 'position',
                      render: (rowData) => (
                        <div>
                          {rowData.position === null ? 'N/A' : rowData.position}
                        </div>
                      )
                    }
                  ]}
                  data={state.recoverableCharts}
                  options={{
                    selection: true,
                    search: false,
                    paging: false,
                    toolbar: false
                  }}
                  components={{ OverlayLoading: () => <div /> }}
                  onSelectionChange={(rows) => {
                    const selectedCharts = rows.map((row) => row.id);
                    setState({
                      ...state,
                      selectedRecoverableCharts: selectedCharts
                    });
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button color="secondary" onClick={onRecoveryCancel}>
                  Cancel
                </Button>

                <Button
                  color="primary"
                  onClick={recoverCharts}
                  disabled={!state.selectedRecoverableCharts.length > 0}
                  autoFocus>
                  Recover
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              fullWidth
              maxWidth="md"
              open={openMetric}
              onClose={onRecoveryCancel}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description">
              <DialogTitle id="alert-dialog-title">
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    paddingLeft: '30px',
                    marginTop: '10px'
                  }}>
                  <SettingsIcon
                    style={{
                      color: '#387EC7',

                      marginRight: '8px'
                    }}
                  />
                  <Typography
                    style={{
                      color: '#387EC7',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      fontFamily: 'Open Sans'
                    }}>
                    Select My Metrics
                  </Typography>
                </Box>
              </DialogTitle>

              <DialogContent
                style={{ paddingLeft: '55px', paddingRight: '55px' }}>
                {clonedSelected ? (
                  <div className="row row-gutters-8 has-metrics-cards">
                    {defaultMetrics.map((s, i) => (
                      <div id={i + 10} key={i + 2} className="col-md-2">
                        <MetricPanelCard
                          img={METRICS_IMAGES[s.tableauImageId]}
                          title={s.displayText}
                          id={i + 1000}
                          checked={clonedSelected.has(s.displayText)}
                          getValue={(isChecked) => handleMetricSelect(s.displayText, isChecked)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <h4>There are no metrics available now...</h4>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  color="secondary"
                  onClick={() => {
                    setOpenMetric(false);
                  }}>
                  Cancel
                </Button>

                <Button color="primary" onClick={handleSaveClick} autoFocus>
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
    </Box>
  );
};

export default Reports;
